import "./Modals.scss";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
} from "antd";
import { Dayjs } from "dayjs";
import { Col, Row } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Company, Department, ModalProps, User } from "../../utils/types";
import {
  employeeTypes,
  fetchData,
  getErrorMessage,
  isBooleanDropdown,
  Notify,
  postData,
  toDropdownListFormat,
  updateData,
  userRoles,
} from "../../utils/constants";
import SearchInput from "../Search/SearchInput";

type UserModalProps = ModalProps & {
  user: User | null;
  refreshData: (name: string) => Promise<void>;
};

const UserModal = ({
  isOpen,
  setIsOpen,
  user,
  refreshData,
}: UserModalProps) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [timekeepers, setTimekeepers] = useState<User[]>([]);
  const [modalLoading, setModalLoading] = useState(true);

  const initCompany = useCallback(
    (companyName: string) => {
      if (!companyName || !companies) return;

      const company = companies.find((c) => c.name === companyName);
      if (!company) return;

      setCompany(company.id);
    },
    [companies],
  );

  const initDepartment = useCallback(
    (deptName: string | null) => {
      if (!deptName || !departments) return;

      const dept = departments.find((d) => d.nameDepartment === deptName);
      if (!dept) return;

      setDepartment(dept.id);
    },
    [departments],
  );

  const findUserByFullName = useCallback(
    (users: User[], fullName: string | null): User | null => {
      if (!fullName) return null;

      const normalizedTarget = fullName.toLowerCase().trim();

      return (
        users.find((u) => {
          const full1 = `${u.firstName} ${u.lastName}`.toLowerCase().trim();
          const full2 = `${u.lastName} ${u.firstName}`.toLowerCase().trim();
          return full1 === normalizedTarget || full2 === normalizedTarget;
        }) || null
      );
    },
    [],
  );

  const initManager = useCallback(
    (fullName: string | null) => {
      const match = findUserByFullName(managers, fullName);
      setManager(match || null);
      setManagerSearchValue(
        match ? `${match.firstName} ${match.lastName}` : "",
      );
    },
    [managers, findUserByFullName],
  );

  const initTimekeeper = useCallback(
    (fullName: string | null) => {
      const match = findUserByFullName(timekeepers, fullName);
      setTimekeeper(match || null);
      setTimekeeperSearchValue(
        match ? `${match.firstName} ${match.lastName}` : "",
      );
    },
    [timekeepers, findUserByFullName],
  );

  const [userEmail, setUserEmail] = useState<string>(user ? user.email : "");
  const [firstName, setFirstName] = useState<string>(
    user ? user.firstName : "",
  );
  const [lastName, setLastName] = useState<string>(user ? user.lastName : "");
  const [employeeType, setEmployeeType] = useState(
    user ? user.employeeType : employeeTypes[0].value,
  );
  const [jobTitle, setJobTitle] = useState<string>(user ? user.jobTitle : "");
  const [workingHours, setWorkingHours] = useState<number>(
    user ? user.workHours : 8,
  );
  const [userGender, setUserGender] = useState<string>(user ? user.gender : "");
  const [company, setCompany] = useState<number | "">("");
  const [department, setDepartment] = useState<number | "">("");
  const [manager, setManager] = useState<User | null>(null);
  const [managerSearchValue, setManagerSearchValue] = useState("");
  const [timekeeper, setTimekeeper] = useState<User | null>(null);
  const [timekeeperSearchValue, setTimekeeperSearchValue] = useState("");

  const [isBaseContract, setIsBaseContract] = useState<boolean>(
    user ? user.baseContract : true,
  );
  // Only for a new user - later employments are registered via "Rehire".
  // The Active status is never edited here: it follows the employment dates.
  const [employmentStartDate, setEmploymentStartDate] = useState<Dayjs | null>(
    null,
  );
  const [roles, setRoles] = useState<string[]>(
    user ? user.roles : [userRoles[0].value],
  );
  const [currentYearDays, setCurrentYearDays] = useState<number | null>(
    user ? user.currentYearDays : null,
  );
  const [previousYearDays, setPreviousYearDays] = useState<number>(
    user ? user.previousYearDays : 0,
  );
  const [entitlement, setEntitlement] = useState<number | null>(
    user ? user.entitlement : null,
  );
  const [employeeCode, setEmployeeCode] = useState(
    user ? user.employeeCode : "",
  );

  const fetchManagers = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/displayAllUsers`,
        setManagers,
      );
    } catch (error) {
      console.error("Error while fetching managers:", error);
      Notify("error", "Failed to fetch managers.");
    }
  }, []);

  const fetchTimekeepers = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/displayAllUsers`,
        setTimekeepers,
      );
    } catch (error) {
      console.error("Error while fetching timekeepers:", error);
      Notify("error", "Failed to fetch timekeepers.");
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/companies/getAllCompanies`,
        setCompanies,
      );
    } catch (error) {
      console.error("Error while fetching companies:", error);
      Notify("error", "Failed to fetch companies.");
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/departments/allAllDepartments`,
        setDepartments,
      );
    } catch (error) {
      console.error("Error while fetching departments:", error);
      Notify("error", "Failed to fetch departments.");
    }
  }, []);

  useEffect(() => {
    if (isOpen === false) return;
    fetchManagers();
    fetchTimekeepers();
    fetchCompanies();
  }, [isOpen, fetchManagers, fetchTimekeepers, fetchCompanies]);

  useEffect(() => {
    if (isOpen === false) return;

    fetchDepartments();
  }, [companies, isOpen, fetchDepartments]);

  const handleSubmit = useCallback(async () => {
    const isEdit = !!user?.id;

    if (entitlement == null || currentYearDays == null) {
      Notify("error", "Entitlement and Current Year Days are required.");
      return;
    }

    if (!isEdit && !employmentStartDate) {
      Notify("error", "Employment start date is required.");
      return;
    }

    const formData = {
      employeeCode,
      email: userEmail,
      firstName,
      lastName,
      employeeType,
      jobTitle,
      roles,
      workHours: workingHours,
      companyId: company || null,
      departmentId: department || null,
      timekeeperId: timekeeper?.id,
      baseContract: isBaseContract,
      managerId: manager?.id,
      employmentStartDate:
        !isEdit && employmentStartDate
          ? employmentStartDate.format("YYYY-MM-DD")
          : undefined,
      currentYearDays,
      previousYearDays,
      entitlement,
      gender: userGender,
    };

    const url = isEdit
      ? `${process.env.REACT_APP_API_URL}/users/updateUser/${user.id}`
      : `${process.env.REACT_APP_API_URL}/users/createNewUser`;

    try {
      isEdit ? await updateData(url, formData) : await postData(url, formData);
      Notify("success", `User ${isEdit ? "updated" : "created"} successfully!`);
      setIsOpen(false);
      await refreshData(isEdit ? "all" : "companies");
    } catch (error) {
      console.error(`${isEdit ? "PUT" : "POST"} error:`, error);
      Notify(
        "error",
        getErrorMessage(
          error,
          `Oops! Something went wrong while ${isEdit ? "updating" : "creating"} user.`,
        ),
      );
    }
  }, [
    company,
    currentYearDays,
    previousYearDays,
    entitlement,
    department,
    employeeCode,
    employeeType,
    firstName,
    isBaseContract,
    jobTitle,
    lastName,
    manager,
    refreshData,
    roles,
    setIsOpen,
    employmentStartDate,
    timekeeper,
    user,
    userEmail,
    workingHours,
    userGender,
  ]);

  useEffect(() => {
    if (!user || !isOpen) return;

    const allDataLoaded =
      companies.length > 0 &&
      departments.length > 0 &&
      managers.length > 0 &&
      timekeepers.length > 0;

    if (!allDataLoaded) {
      setModalLoading(true);
      return;
    }


    setUserEmail(user.email || "");
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setEmployeeType(user.employeeType || employeeTypes[0].value);
    setJobTitle(user.jobTitle || "");
    setWorkingHours(user.workHours || 8);
    setIsBaseContract(user.baseContract ?? true);
    setCurrentYearDays(user.currentYearDays ?? null);
    setPreviousYearDays(user.previousYearDays || 0);
    setEntitlement(user.entitlement ?? null);
    setEmployeeCode(user.employeeCode || "");
    setRoles(user.roles || [userRoles[0].value]);

    initCompany(user.companyName);
    initDepartment(user.departmentName);
    initManager(user.managerFullName);
    initTimekeeper(user.timekeeperFullName);
    setUserGender(user.gender);
    setModalLoading(false);

  }, [
    isOpen,
    user,
    companies,
    departments,
    managers,
    timekeepers,
    initCompany,
    initDepartment,
    initManager,
    initTimekeeper,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setUserEmail("");
      setFirstName("");
      setLastName("");
      setEmployeeType(employeeTypes[0].value);
      setJobTitle("");
      setWorkingHours(8);
      setIsBaseContract(true);
      setEmploymentStartDate(null);
      setCurrentYearDays(null);
      setPreviousYearDays(0);
      setEntitlement(null);
      setEmployeeCode("");
      setRoles([userRoles[0].value]);

      setCompany("");
      setDepartment("");
      setManager(null);
      setManagerSearchValue("");
      setTimekeeper(null);
      setTimekeeperSearchValue("");
      setUserGender("");
    }
  }, [isOpen]);

  return (
    <Modal
      className="lg"
      width={760}
      open={isOpen}
      title={user?.id ? "Update user" : "Create new user"}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {user?.id ? "Update user" : "Create user"}
        </Button>,
      ]}
    >
      {modalLoading && user ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <Spin />
          <p>Loading user data...</p>
        </div>
      ) : (
        <Form layout="vertical" requiredMark>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Firstname" required>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Lastname" required>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Gender" required>
                <Select
                  value={userGender}
                  options={[
                    { label: "Male", value: "male" },
                    { label: "Female", value: "female" },
                  ]}
                  onChange={(value) => setUserGender(value)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="User email" required>
                <Input
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Working Hours" required>
                <InputNumber
                  min={0}
                  value={workingHours}
                  onChange={(value) =>
                    setWorkingHours(typeof value === "number" ? value : 0)
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Employee type" required>
                <Select
                  value={employeeType}
                  options={employeeTypes.map((item) => ({
                    label: item.text,
                    value: item.value,
                  }))}
                  onChange={(value) => setEmployeeType(value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item className="nowrap" label="Employee Code" required>
                <Input
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Job Title" required>
                <Input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Company" required>
                <Select
                  value={company}
                  options={toDropdownListFormat(companies, "name", "id").map(
                    (item) => ({
                      label: item.text,
                      value: item.value,
                    }),
                  )}
                  onChange={(value) => setCompany(Number(value))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Department" required>
                <Select
                  value={department}
                  options={toDropdownListFormat(
                    departments,
                    "nameDepartment",
                    "id",
                  ).map((item) => ({
                    label: item.text,
                    value: item.value,
                  }))}
                  onChange={(value) => setDepartment(Number(value))}
                  disabled={companies.length === 0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Manager" required>
                <SearchInput
                  onSelect={(u) => {
                    setManager(u);
                    setManagerSearchValue(
                      u ? `${u.firstName} ${u.lastName}` : "",
                    );
                  }}
                  options={managers}
                  toDisplay={["firstName", "lastName"]}
                  value={managerSearchValue}
                  onChangeValue={setManagerSearchValue}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Timekeeper">
                <SearchInput
                  onSelect={(u) => {
                    setTimekeeper(u);
                    setTimekeeperSearchValue(
                      u ? `${u.firstName} ${u.lastName}` : "",
                    );
                  }}
                  options={timekeepers}
                  toDisplay={["firstName", "lastName"]}
                  value={timekeeperSearchValue}
                  onChangeValue={setTimekeeperSearchValue}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Base Contract" required>
                <Select
                  value={isBaseContract}
                  options={isBooleanDropdown.map((item) => ({
                    label: item.text,
                    value: item.value,
                  }))}
                  onChange={(value) => setIsBaseContract(value)}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              {user?.id ? (
                <Form.Item
                  label="Active"
                  tooltip="Follows the employment dates. Use Terminate / Rehire in the users list to change it."
                >
                  <Input
                    disabled
                    value={user.status === "ACTIVE" ? "Yes" : "No"}
                  />
                </Form.Item>
              ) : (
                <Form.Item label="Employment start date" required>
                  <DatePicker
                    format="DD.MM.YYYY"
                    value={employmentStartDate}
                    onChange={(value) => setEmploymentStartDate(value)}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </Col>

            <Col span={6}>
              <Form.Item label="Current Year Days" required>
                <InputNumber
                  min={0}
                  value={currentYearDays}
                  onChange={(value) =>
                    setCurrentYearDays(typeof value === "number" ? value : null)
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Previous Year Days">
                <InputNumber
                  min={0}
                  value={previousYearDays}
                  onChange={(value) =>
                    setPreviousYearDays(typeof value === "number" ? value : 0)
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Entitlement" required>
                <InputNumber
                  min={0}
                  value={entitlement}
                  onChange={(value) =>
                    setEntitlement(typeof value === "number" ? value : null)
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Role" required>
            <Select
              mode="multiple"
              value={roles}
              options={userRoles.map((item) => ({
                label: item.text,
                value: item.value,
              }))}
              onChange={(value) => setRoles(value)}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UserModal;
